/**
 * VH-83: AI-generate prompt form. Submits to /recipes/ai-generate, then hands
 * the resulting RecipeDraft to the parent (which renders RecipeDraftReview).
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SparklesIcon } from '@heroicons/react/24/outline'
import EquipmentTagsInput from './EquipmentTagsInput'

export default function CaptureAIPromptForm({ onGenerate, generating, error }) {
  const { t } = useTranslation()
  const [form, setForm] = useState({
    prompt: '',
    max_kcal_per_serving: '',
    min_protein_per_serving_g: '',
    max_total_minutes: '',
    suggested_servings: '',
    allowed_equipment_keys: [],
  })
  const [localError, setLocalError] = useState(null)

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!form.prompt.trim()) {
      setLocalError(t('recipes.ai.promptRequired'))
      return
    }
    setLocalError(null)
    const payload = {
      prompt: form.prompt.trim(),
    }
    if (form.max_kcal_per_serving)
      payload.max_kcal_per_serving = parseInt(form.max_kcal_per_serving, 10)
    if (form.min_protein_per_serving_g)
      payload.min_protein_per_serving_g = parseInt(
        form.min_protein_per_serving_g,
        10
      )
    if (form.max_total_minutes)
      payload.max_total_minutes = parseInt(form.max_total_minutes, 10)
    if (form.suggested_servings)
      payload.suggested_servings = parseInt(form.suggested_servings, 10)
    if (form.allowed_equipment_keys.length > 0)
      payload.allowed_equipment_keys = form.allowed_equipment_keys
    onGenerate?.(payload)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="form-label">{t('recipes.ai.promptField')}</label>
        <textarea
          rows={3}
          value={form.prompt}
          onChange={(e) => setForm({ ...form, prompt: e.target.value })}
          placeholder={t('recipes.ai.promptPlaceholder')}
          className="form-input w-full"
          data-testid="ai-prompt"
        />
        {localError && <p className="form-error">{localError}</p>}
      </div>

      <details className="rounded-md border border-gray-200">
        <summary className="cursor-pointer px-3 py-2 text-sm text-gray-700 select-none">
          {t('recipes.ai.constraintsTitle')}
        </summary>
        <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="form-label">
              {t('recipes.ai.maxKcalField')}
            </label>
            <input
              type="number"
              min="0"
              value={form.max_kcal_per_serving}
              onChange={(e) =>
                setForm({ ...form, max_kcal_per_serving: e.target.value })
              }
              className="form-input w-full"
            />
          </div>
          <div>
            <label className="form-label">
              {t('recipes.ai.minProteinField')}
            </label>
            <input
              type="number"
              min="0"
              value={form.min_protein_per_serving_g}
              onChange={(e) =>
                setForm({
                  ...form,
                  min_protein_per_serving_g: e.target.value,
                })
              }
              className="form-input w-full"
            />
          </div>
          <div>
            <label className="form-label">
              {t('recipes.ai.maxMinutesField')}
            </label>
            <input
              type="number"
              min="1"
              value={form.max_total_minutes}
              onChange={(e) =>
                setForm({ ...form, max_total_minutes: e.target.value })
              }
              className="form-input w-full"
            />
          </div>
          <div>
            <label className="form-label">
              {t('recipes.ai.servingsField')}
            </label>
            <input
              type="number"
              min="1"
              value={form.suggested_servings}
              onChange={(e) =>
                setForm({ ...form, suggested_servings: e.target.value })
              }
              className="form-input w-full"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="form-label">
              {t('recipes.ai.allowedEquipmentField')}
            </label>
            <EquipmentTagsInput
              value={form.allowed_equipment_keys}
              onChange={(keys) =>
                setForm({ ...form, allowed_equipment_keys: keys })
              }
            />
          </div>
        </div>
      </details>

      {error && (
        <p className="text-sm text-red-600" data-testid="ai-error">
          {error}
        </p>
      )}

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={generating}
          className="btn-primary flex items-center gap-2"
          data-testid="ai-generate-submit"
        >
          <SparklesIcon className="h-4 w-4" />
          {generating ? t('recipes.ai.generating') : t('recipes.ai.generate')}
        </button>
      </div>
    </form>
  )
}
